import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CheckInData {
  id: string;
  user_id: string;
  garita: string;
  lane_type: string;
  mode: string;
  created_at: string;
  quality_score?: number;
}

interface CheckOutData {
  id: string;
  user_id: string;
  checkin_id: string;
  created_at: string;
}

interface WaitTimeData {
  crossing: string;
  laneType: string;
  mode: string;
  currentWaitTime: number;
  lastUpdated: Date;
  reportCount: number;
}

interface CrossingWaitTimes {
  [key: string]: WaitTimeData;
}

export const useRealTimeWaits = () => {
  const [waitTimes, setWaitTimes] = useState<CrossingWaitTimes>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const { toast } = useToast();

  const calculateWaitTime = useCallback(async () => {
    setLoading(true);
    
    try {
      const now = new Date();
      const fourHoursAgo = new Date(now.getTime() - 4 * 60 * 60 * 1000);

      // Fetch check-ins from the last 4 hours with quality scores
      const { data: checkIns, error: checkInError } = await supabase
        .from('checkins')
        .select('id, user_id, garita, lane_type, mode, created_at, quality_score, geofence_match, gps_accuracy_meters, verified_by_photo')
        .gte('created_at', fourHoursAgo.toISOString())
        .order('created_at', { ascending: false }) as { data: CheckInData[] | null, error: any };

      if (checkInError) {
        console.error('Error fetching check-ins:', checkInError);
        return;
      }

      // Fetch check-outs
      const { data: checkOuts, error: checkOutError } = await supabase
        .from('checkouts')
        .select('*')
        .gte('created_at', fourHoursAgo.toISOString());

      if (checkOutError) {
        console.error('Error fetching check-outs:', checkOutError);
        return;
      }

      // Create a map of check-outs by checkin_id
      const checkOutMap = new Map<string, CheckOutData>();
      checkOuts?.forEach(checkout => {
        if (checkout.checkin_id) {
          checkOutMap.set(checkout.checkin_id, checkout);
        }
      });

      // Group submissions by crossing and lane type
      const submissions = new Map<string, Array<{
        checkInTime: Date;
        checkOutTime?: Date;
        waitTime?: number;
        estimatedWaitTime?: number;
        weight: number;
      }>>();

      checkIns?.forEach(checkIn => {
        // Normalize lane type for consistency
        let normalizedLaneType = checkIn.lane_type;
        if (checkIn.lane_type === 'General') {
          normalizedLaneType = 'Normal';
        }
        
        const key = `${checkIn.garita}-${normalizedLaneType}-${checkIn.mode}`;
        const checkInTime = new Date(checkIn.created_at);
        const checkOut = checkOutMap.get(checkIn.id);
        
        let waitTime: number | undefined;
        let estimatedWaitTime: number | undefined;
        let checkOutTime: Date | undefined;

        if (checkOut) {
          // User has checked out - calculate actual wait time
          checkOutTime = new Date(checkOut.created_at);
          waitTime = (checkOutTime.getTime() - checkInTime.getTime()) / 60000; // in minutes
        } else {
          // User has only checked in - estimate current wait time
          estimatedWaitTime = (now.getTime() - checkInTime.getTime()) / 60000; // in minutes
        }

        // Skip if wait time is unrealistic (< 2 minutes or > 6 hours)
        const timeToCheck = waitTime || estimatedWaitTime;
        if (timeToCheck && (timeToCheck < 2 || timeToCheck > 360)) {
          return;
        }

        // Calculate weights with quality scoring
        const referenceTime = checkOutTime || now;
        const minutesAgo = (now.getTime() - referenceTime.getTime()) / 60000;
        const W1 = Math.max(0.05, 1 - (minutesAgo / 240)); // Recency weight
        const W2 = checkOut ? 1.0 : 0.3; // Data quality weight (completed vs ongoing)
        const W3 = checkIn.quality_score || 0.5; // Quality score from database
        const weight = W1 * W2 * W3;

        if (!submissions.has(key)) {
          submissions.set(key, []);
        }

        submissions.get(key)!.push({
          checkInTime,
          checkOutTime,
          waitTime,
          estimatedWaitTime,
          weight
        });
      });

      // Calculate weighted averages for each crossing/lane combination
      const newWaitTimes: CrossingWaitTimes = {};

      submissions.forEach((submissionList, key) => {
        let totalWeightedWait = 0;
        let totalWeight = 0;

        submissionList.forEach(submission => {
          const timeToUse = submission.waitTime || submission.estimatedWaitTime || 0;
          totalWeightedWait += timeToUse * submission.weight;
          totalWeight += submission.weight;
        });

        if (totalWeight > 0) {
          const averageWait = totalWeightedWait / totalWeight;
          const displayWaitTime = Math.round(averageWait);
          
          const [crossing, laneType, mode] = key.split('-');
          newWaitTimes[key] = {
            crossing,
            laneType,
            mode,
            currentWaitTime: displayWaitTime,
            lastUpdated: now,
            reportCount: submissionList.length
          };
        }
      });

      setWaitTimes(newWaitTimes);
      setLastUpdated(now);

    } catch (error) {
      console.error('Failed to calculate wait times:', error);
      toast({
        title: "Wait Time Error",
        description: "Unable to calculate current wait times.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Initial load
  useEffect(() => {
    calculateWaitTime();
  }, [calculateWaitTime]);

  // Set up real-time subscription for new check-outs and minute-based refresh
  useEffect(() => {
    const checkoutChannel = supabase
      .channel('checkout-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'checkouts'
        },
        () => {
          // Recalculate when someone checks out
          calculateWaitTime();
        }
      )
      .subscribe();

    // Recalculate every minute
    const interval = setInterval(() => {
      calculateWaitTime();
    }, 60000);

    return () => {
      supabase.removeChannel(checkoutChannel);
      clearInterval(interval);
    };
  }, [calculateWaitTime]);

  const refreshWaitTimes = () => {
    calculateWaitTime();
  };

  const getWaitTime = (crossing: string, laneType: string, mode: string = 'car'): number => {
    const key = `${crossing}-${laneType}-${mode}`;
    return waitTimes[key]?.currentWaitTime || 0;
  };

  const getWaitTimeColor = (waitTime: number): string => {
    if (waitTime < 30) return "text-success";
    if (waitTime < 60) return "text-warning";
    return "text-destructive";
  };

  const getWaitTimeDot = (waitTime: number): string => {
    if (waitTime < 30) return "bg-success";
    if (waitTime < 60) return "bg-warning";
    return "bg-destructive";
  };

  const getReportCount = (crossing: string, laneType: string, mode: string = 'car'): number => {
    const key = `${crossing}-${laneType}-${mode}`;
    return waitTimes[key]?.reportCount || 0;
  };

  return {
    waitTimes,
    loading,
    lastUpdated,
    refreshWaitTimes,
    getWaitTime,
    getWaitTimeColor,
    getWaitTimeDot,
    getReportCount
  };
};